import type { MutableRefObject } from 'react';
import type {
  AudioCaptureRefs,
  MicPermissionState,
  PermissionResult,
  SpeechWebSocketHandlers,
} from './AIVoiceChatBot.types';

const DEFAULT_SPEECH_WS_URL =
  'wss://vidai-ai.in/speech-to-emr/ws/transcribe';
// 'wss://buys-manitoba-version-trance.trycloudflare.com/speech-to-emr/ws/transcribe';

export const getMicErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    if (error.message === 'WebSocket connection error') {
      return 'WebSocket connection error. Please try again.';
    }

    if (error.message === 'WebSocket connection timeout') {
      return 'Live transcription server took too long to connect.';
    }

    if (error.message === 'WebSocket not connected.') {
      return 'WebSocket not connected. Please try again.';
    }
  }

  if (!(error instanceof DOMException)) {
    return 'Could not access the microphone. Please check permission and try again.';
  }

  switch (error.name) {
  case 'NotAllowedError':
  case 'PermissionDeniedError':
    return 'Microphone permission was denied. Please allow microphone access in your browser settings and try again.';
  case 'NotFoundError':
  case 'DevicesNotFoundError':
    return 'No microphone was found on this device.';
  case 'NotReadableError':
  case 'TrackStartError':
    return 'The microphone is busy or unavailable. Please close other apps using the mic and try again.';
  case 'SecurityError':
    return 'Microphone access is blocked. Open this page in HTTPS or localhost.';
  case 'AbortError':
    return 'Microphone access was interrupted. Please try again.';
  default:
    return 'Could not access the microphone. Please check permission and try again.';
  }
};

export const requestMicrophonePermission = async (
  currentPermission: MicPermissionState
): Promise<PermissionResult> => {
  if (!navigator.mediaDevices?.getUserMedia) {
    return {
      allowed: false,
      permission: 'unsupported',
      message: 'Microphone access is not supported in this browser.',
    };
  }

  if (currentPermission === 'granted') {
    return {
      allowed: true,
      permission: 'granted',
      message: '',
    };
  }

  if (currentPermission === 'denied') {
    return {
      allowed: false,
      permission: 'denied',
      message:
        'Microphone permission is blocked. Please enable it from your browser site settings.',
    };
  }

  try {
    const permissionStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });

    permissionStream.getTracks().forEach((track) => track.stop());

    return {
      allowed: true,
      permission: 'granted',
      message: '',
    };
  } catch (error) {
    const message = getMicErrorMessage(error);

    if (
      error instanceof DOMException &&
      (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')
    ) {
      return {
        allowed: false,
        permission: 'denied',
        message,
      };
    }

    return {
      allowed: false,
      permission: 'prompt',
      message,
    };
  }
};

export const getSpeechWsUrl = (): string => {
  const env =
    (import.meta as ImportMeta & {
      env?: Record<string, string | undefined>;
    }).env ?? {};

  const envBase =
    env.VITE_SPEECH_TO_TEXT_BASE_URL ||
    env.REACT_SPEECH_TO_TEXT_BASE_URL ||
    '';

  if (envBase) {
    const cleaned = envBase.replace(/\/+$/, '');

    if (cleaned.startsWith('wss://') || cleaned.startsWith('ws://')) {
      return `${cleaned}/speech-to-emr/ws/transcribe`;
    }

    if (cleaned.startsWith('https://')) {
      return `${cleaned.replace('https://', 'wss://')}/speech-to-emr/ws/transcribe`;
    }

    if (cleaned.startsWith('http://')) {
      return `${cleaned.replace('http://', 'ws://')}/speech-to-emr/ws/transcribe`;
    }
  }

  return DEFAULT_SPEECH_WS_URL;
};

export const stopAudioCapture = async (refs: AudioCaptureRefs): Promise<void> => {
  const { processorRef, sourceNodeRef, audioContextRef, mediaStreamRef } = refs;

  try {
    processorRef.current?.disconnect();
    sourceNodeRef.current?.disconnect();

    processorRef.current = null;
    sourceNodeRef.current = null;

    if (audioContextRef.current) {
      await audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  } catch {
    // ignore cleanup errors
  }
};

export const createSpeechWebSocket = ({
  url,
  timeoutMs = 8000,
  onOpen,
  onTranscript,
  onCompleted,
  onUnexpectedClose,
  onError,
}: SpeechWebSocketHandlers): Promise<WebSocket> => {
  return new Promise<WebSocket>((resolve, reject) => {
    let settled = false;

    const ws = new WebSocket(url);

    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;

      try {
        ws.close();
      } catch {
        // ignore
      }

      reject(new Error('WebSocket connection timeout'));
    }, timeoutMs);

    const safeResolve = (socket: WebSocket) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(socket);
    };

    const safeReject = (error: Error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      reject(error);
    };

    ws.onopen = () => {
      onOpen?.(ws);
      safeResolve(ws);
    };

    ws.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);

        if (typeof data.transcript === 'string') {
          onTranscript?.(data.transcript);
        }

        if (data.status === 'completed') {
          const completedTranscript =
            typeof data.transcript === 'string' ? data.transcript : '';
          await onCompleted?.(completedTranscript);
        }
      } catch {
        // ignore malformed websocket messages
      }
    };

    ws.onerror = () => {
      onError?.();
      safeReject(new Error('WebSocket connection error'));
    };

    ws.onclose = async () => {
      window.clearTimeout(timeoutId);

      if (!settled) {
        safeReject(new Error('WebSocket connection error'));
      }

      await onUnexpectedClose?.();
    };
  });
};

export const startAudioStreaming = async (
  ws: WebSocket,
  refs: AudioCaptureRefs,
  isRecordingRef: MutableRefObject<boolean>
): Promise<void> => {
  const mediaStream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      sampleRate: 16000,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });

  refs.mediaStreamRef.current = mediaStream;

  const AudioContextClass =
    window.AudioContext ||
    (
      window as Window &
      typeof globalThis & {
        webkitAudioContext?: typeof AudioContext;
      }
    ).webkitAudioContext;

  if (!AudioContextClass) {
    throw new Error('AudioContext is not supported in this browser.');
  }

  const audioContext = new AudioContextClass({ sampleRate: 16000 });
  refs.audioContextRef.current = audioContext;

  const source = audioContext.createMediaStreamSource(mediaStream);
  refs.sourceNodeRef.current = source;

  const processor = audioContext.createScriptProcessor(4096, 1, 1);
  refs.processorRef.current = processor;

  processor.onaudioprocess = (e: AudioProcessingEvent) => {
    if (!isRecordingRef.current || ws.readyState !== WebSocket.OPEN) return;

    const inputData = e.inputBuffer.getChannelData(0);
    const int16Data = new Int16Array(inputData.length);

    for (let i = 0; i < inputData.length; i += 1) {
      const s = Math.max(-1, Math.min(1, inputData[i]));
      int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }

    ws.send(int16Data.buffer);
  };

  source.connect(processor);
  processor.connect(audioContext.destination);
};